import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaMapLocationDot,
  FaUser,
  FaNetworkWired,
  FaBox,
  FaPlus,
} from "react-icons/fa6";
import { LayersControl, MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { SelectOdp } from "../../../../components/forms/selects/SelectOdp";
import { SelectProfile } from "../../../../components/forms/selects/SelectProfile";
import { InputDefault } from "../../../../components/forms/InputDefault";
import { Header } from "../../../../components/Header";
import { handleDetectGPS } from "../../../../utilities/leftjs/handleDetectGps";
import { FiCrosshair, FiLoader, FiTrash2 } from "react-icons/fi";
import { getLocationDetail } from "../../../../utilities/leftjs/getLocationDetail";
import { MapSync } from "../../../../utilities/leftjs/MapSync";
import { MapController } from "../../../../utilities/leftjs/MapController";
import { PppoeUserService } from "../../../../services/PppoeUserService";
import { SelectOdc } from "../../../../components/forms/selects/SelectOdc";
import { SelectCentralOdc } from "../../../../components/forms/selects/SelectCentralOdc";
import { SelectOlt } from "../../../../components/forms/selects/SelectOlt";
import { TextAreaDefault } from "../../../../components/forms/TextAreaDefault";
import { MapView } from "../../../../utilities/leftjs/MapView";

const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

// Lokasi default sebagai fallback
const DEFAULT_CENTER = { lat: -6.17511, lng: 106.827153 };

export const PppoeUserCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [remainingSlots, setRemainingSlots] = useState(3);
  const [previews, setPreviews] = useState([]);

  // State sesuai database schema
  const [formData, setFormData] = useState({
    // Identitas
    internet_number: "",
    nik: "",
    name: "",
    phone_number: "",
    username: "",
    password: "",
    wifi_name: "",
    wifi_password: "",

    // Paket & Harga
    profile_id: "",
    price: 0,
    discount: 0,
    total_price: 0,
    mitra_id: "",
    reseller_id: "",

    // Tanggal
    registration_date: "",
    due_date: "",

    // Mapping Jalur
    pop_id: "",
    nas_id: "",
    olt_id: "",
    central_odc_id_id: "",
    odc_id: "",
    odp_id: "",

    // Lokasi
    latitude: DEFAULT_CENTER.lat,
    longitude: DEFAULT_CENTER.lng,
    address: "",
    location_detail: null,

    // Status
    status: "aktif",
  });

  // Error state
  const [error, setError] = useState({
    internet_number: "",
    nik: "",
    name: "",
    phone_number: "",
    username: "",
    password: "",
    wifi_name: "",
    wifi_password: "",

    profile_id: "",
    mitra_id: "",
    reseller_id: "",

    pop_id: "",
    nas_id: "",
    olt_id: "",
    central_odc_id_id: "",
    odc_id: "",
    odp_id: "",
  });

  // Handlers
  const handleChange = (e) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "number" ? (value === "" ? 0 : Number(value)) : value,
    }));

    // Clear error saat user mengetik
    if (error[id]) {
      setError((prev) => ({ ...prev, [id]: "" }));
    }
  };

  // Hitung total harga
  useEffect(() => {
    const price = Number(formData.price) || 0;
    const discount = Number(formData.discount) || 0;
    const total = price - discount;

    setFormData((prev) => ({
      ...prev,
      total_price: total < 0 ? 0 : total,
    }));
  }, [formData.price, formData.discount]);

  // Cleanup URL object saat komponen unmount atau file berubah
  useEffect(() => {
    return () => {
      previews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previews]);

  const handleFileChange = (e) => {
    try {
      const files = Array.from(e.target.files || []);

      if (files.length > remainingSlots) {
        alert(`Maksimal unggah ${remainingSlots} foto lagi.`);
        e.target.value = null;
        return;
      }

      const newPreviews = files
        .map((file) => {
          try {
            return URL.createObjectURL(file);
          } catch (err) {
            console.warn("Gagal membuat preview untuk file:", err);
            return null;
          }
        })
        .filter(Boolean);

      setSelectedFiles((prev) => [...prev, ...files].slice(0, remainingSlots));
      setPreviews((prev) => [...prev, ...newPreviews].slice(0, remainingSlots));
      e.target.value = null;
    } catch (error) {
      console.warn("Gagal memproses file:", error);
    }
  };

  const removeSelectedFile = (index) => {
    try {
      if (previews[index]) {
        URL.revokeObjectURL(previews[index]);
      }
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      setPreviews((prev) => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.warn("Gagal menghapus file:", error);
    }
  };

  const [loadingGps, setLoadingGps] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [gpsInput, setGpsInput] = useState(
    `${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`,
  );
  const [gpsError, setGpsError] = useState(null);

  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);

    // Parse string "lat, lng" menjadi angka
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());

      // Validasi apakah angka yang valid
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        // Reset error jika user berhasil input manual
        setGpsError(null);
      }
    }
  };

  const handleLocationUpdate = async (lat, lng) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));

    // 3. Fetch Alamat (Reverse Geocoding)
    setFetchingAddress(true);
    try {
      const locationData = await getLocationDetail(lat, lng);
      if (locationData && locationData.displayName) {
        setFormData((prev) => ({
          ...prev,
          address: locationData.displayName,
        }));
      }
    } catch (err) {
      console.error("Gagal mengambil alamat:", err);
    } finally {
      setFetchingAddress(false);
    }
  };

  // Fungsi untuk deteksi GPS dengan error handling lengkap
  const safeDetectGps = async () => {
    setLoadingGps(true);
    setGpsError(null);

    try {
      await handleDetectGPS({
        setLoading: setLoadingGps,
        handleLocationUpdate: handleLocationUpdate,
      });
    } catch (error) {
      console.warn(
        "GPS tidak tersedia atau gagal, menggunakan lokasi default:",
        error.message || error,
      );

      let errorMessage = "GPS tidak tersedia";
      if (error.code === 1) {
        errorMessage = "Akses lokasi ditolak oleh browser";
      } else if (error.code === 2) {
        errorMessage = "Lokasi tidak tersedia saat ini";
      } else if (error.code === 3) {
        errorMessage = "Waktu habis saat mendeteksi lokasi";
      } else if (error.message?.includes("permission")) {
        errorMessage = "Izin akses lokasi belum diberikan";
      }

      setGpsError(errorMessage);

      setFormData((prev) => ({
        ...prev,
        latitude: DEFAULT_CENTER.lat,
        longitude: DEFAULT_CENTER.lng,
      }));
      // Update input string juga
      setGpsInput(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
    } finally {
      setLoadingGps(false);
    }
  };

  // Deteksi GPS saat komponen mount
  useEffect(() => {
    safeDetectGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validasi form
  const validateForm = () => {
    let isValid = true;
    const newError = {
      internet_number: "",
      name: "",
      phone_number: "",
      username: "",
      password: "",
      profile_id: "",
      nas_id: "",
      pop_id: "",
      odp_id: "",
    };

    if (!formData.internet_number?.trim()) {
      newError.internet_number = "ID Pelanggan wajib diisi";
      isValid = false;
    }

    if (!formData.name?.trim()) {
      newError.name = "Nama pelanggan wajib diisi";
      isValid = false;
    }

    if (
      formData.phone_number &&
      !/^[0-9+]{10,15}$/.test(formData.phone_number)
    ) {
      newError.phone_number = "Format nomor tidak valid (10-15 digit)";
      isValid = false;
    }

    if (!formData.username?.trim()) {
      newError.username = "Username wajib diisi";
      isValid = false;
    } else if (formData.username.length < 3) {
      newError.username = "Username minimal 3 karakter";
      isValid = false;
    } else if (!/^[a-zA-Z0-9_.-]+$/.test(formData.username)) {
      newError.username = "Username hanya boleh huruf, angka, _ . -";
      isValid = false;
    }

    if (!formData.password?.trim()) {
      newError.password = "Password wajib diisi";
      isValid = false;
    } else if (formData.password.length < 4) {
      newError.password = "Password minimal 4 karakter";
      isValid = false;
    }

    if (!formData.profile_id?.trim()) {
      newError.profile_id = "Paket/Profile wajib dipilih";
      isValid = false;
    }

    if (!formData.nas_id?.trim()) {
      newError.nas_id = "NAS wajib dipilih";
      isValid = false;
    }

    if (!formData.pop_id?.trim()) {
      newError.pop_id = "POP wajib dipilih";
      isValid = false;
    }

    if (!formData.odp_id?.trim()) {
      newError.odp_id = "ODP wajib dipilih";
      isValid = false;
    }

    setError(newError);
    return isValid;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Mencegah event bubbling ke map
    setLoading(true);

    // Reset errors
    setError({
      internet_number: "",
      nik: "",
      name: "",
      phone_number: "",
      username: "",
      password: "",
      wifi_name: "",
      wifi_password: "",

      profile_id: "",
      mitra_id: "",
      reseller_id: "",

      pop_id: "",
      nas_id: "",
      olt_id: "",
      central_odc_id_id: "",
      odc_id: "",
      odp_id: "",
    });

    // if (!validateForm()) {
    //   setLoading(false);
    //   return;
    // }

    try {
      const submitData = {
        ...formData,
        location_detail: formData.location_detail
          ? JSON.stringify(formData.location_detail)
          : null,
      };

      await PppoeUserService.create({ data: formData });
      navigate("/dashboard/pppoe/user");
    } catch (error) {
      console.error("Create PPPoE User Error:", error);

      // Handle backend validation errors
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        setError((prev) => ({
          ...prev,
          ...Object.keys(backendErrors).reduce((acc, key) => {
            if (prev.hasOwnProperty(key)) {
              acc[key] = Array.isArray(backendErrors[key])
                ? backendErrors[key][0]
                : backendErrors[key];
            }
            return acc;
          }, {}),
        }));
      }

      // Handle unique constraint
      if (error.response?.data?.message?.toLowerCase().includes("username")) {
        setError((prev) => ({
          ...prev,
          username: "Username sudah digunakan",
        }));
      }

      if (
        error.response?.data?.message?.toLowerCase().includes("internet_number")
      ) {
        setError((prev) => ({
          ...prev,
          internet_number: "ID Pelanggan sudah terdaftar",
        }));
      }

      // General error
      if (!error.response?.data?.errors && !error.response?.data?.message) {
        alert("Gagal menyimpan data. Silakan coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tambah Pengguna PPPoE - DANELA NET</title>
        <meta name="description" content="Form tambah pengguna PPPoE baru." />
      </Helmet>

      <div className="relative">
        {/* HEADER */}
        <Header showToggleSidebar={false}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-700/50 rounded-full transition-all text-slate-400 hover:text-white"
            >
              <FaChevronLeft />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">
                Tambah Pengguna PPPoE
              </h2>
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                Form Input Pelanggan Baru
              </p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6 relative z-10">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
            id="pppoe-form"
          >
            {/* ============================================ */}
            {/* SECTION 1: DATA PELANGGAN & DATA PAKET */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* DATA PELANGGAN */}
              <div
                className="lg:col-span-7 rounded-2xl p-5 lg:p-6"
                style={CARD_STYLE}
              >
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                  <FaUser className="text-cyan-500 text-[10px]" />
                  Data Pelanggan
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputDefault
                    id="internet_number"
                    label="ID Pelanggan"
                    type="text"
                    placeholder="Contoh: PEL-001"
                    value={formData.internet_number}
                    onChange={handleChange}
                    error={error.internet_number}
                    required
                  />

                  <InputDefault
                    id="nik"
                    label="NIK Pelanggan"
                    subLabel={"Opsional"}
                    type="text"
                    placeholder="Masukkan nik pelanggan..."
                    value={formData.nik}
                    onChange={handleChange}
                    error={error.nik}
                    required
                  />

                  <InputDefault
                    id="name"
                    label="Nama Pelanggan"
                    type="text"
                    placeholder="Masukkan nama pelanggan..."
                    value={formData.name}
                    onChange={handleChange}
                    error={error.name}
                    required
                  />

                  <InputDefault
                    id="phone_number"
                    label="No. Telepon"
                    subLabel={"Opsional"}
                    type="text"
                    inputMode="tel"
                    placeholder="08xxxxxxxxxx"
                    value={formData.phone_number}
                    onChange={handleChange}
                    error={error.phone_number}
                  />

                  <InputDefault
                    id="username"
                    label="Username PPPoE"
                    type="text"
                    placeholder="Masukkan username..."
                    value={formData.username}
                    onChange={handleChange}
                    error={error.username}
                    required
                  />

                  <InputDefault
                    id="password"
                    label="Password PPPoE"
                    type="text"
                    placeholder="Masukkan password..."
                    value={formData.password}
                    onChange={handleChange}
                    error={error.password}
                    required
                  />

                  <InputDefault
                    id="wifi_name"
                    label="Nama Wifi"
                    type="text"
                    placeholder="Masukkan username..."
                    value={formData.wifi_username}
                    onChange={handleChange}
                    error={error.wifi_username}
                    required
                  />

                  <InputDefault
                    id="wifi_password"
                    label="Password Wifi"
                    type="text"
                    placeholder="Masukkan password..."
                    value={formData.wifi_password}
                    onChange={handleChange}
                    error={error.wifi_password}
                    required
                  />
                </div>
              </div>

              {/* DATA PAKET */}
              <div
                className="lg:col-span-5 rounded-2xl p-5 lg:p-6"
                style={CARD_STYLE}
              >
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                  <FaBox className="text-purple-500 text-[10px]" />
                  Data Paket
                </h3>

                <div className="flex flex-col gap-4">
                  <SelectProfile
                    label="Pilih Profile / Paket"
                    placeholder="Silahkan pilih paket..."
                    value={formData.profile_id}
                    setValue={(value) =>
                      setFormData((prev) => ({ ...prev, profile_id: value }))
                    }
                    setPrice={(price) =>
                      setFormData((prev) => ({
                        ...prev,
                        price: Number(price) || 0,
                      }))
                    }
                    error={error.profile_id}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <InputDefault
                      id="price"
                      label="Harga Paket"
                      type="number"
                      placeholder="0"
                      value={formData.price}
                      onChange={handleChange}
                      disabled={true}
                    />

                    <InputDefault
                      id="discount"
                      label="Diskon"
                      type="number"
                      inputMode="numeric"
                      placeholder="Masukkan diskon..."
                      value={formData.discount}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/30">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Total Harga
                      </span>
                      <span className="text-xl font-black text-cyan-400">
                        Rp {formData.total_price?.toLocaleString("id-ID") || 0}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <InputDefault
                      id="registration_date"
                      label="Tgl. Registrasi"
                      type="date"
                      value={formData.registration_date}
                      onChange={handleChange}
                    />

                    <InputDefault
                      id="due_date"
                      label="Tgl. Tagihan"
                      type="date"
                      value={formData.due_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* SECTION 2: DATA JALUR */}
            {/* ============================================ */}
            <div className="rounded-2xl p-5 lg:p-6" style={CARD_STYLE}>
              <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <FaNetworkWired className="text-emerald-500 text-[10px]" />
                Data Jalur / Mapping
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <SelectPop
                  label="Pop"
                  placeholder="Silahkan pilih Pop"
                  value={formData.pop_id}
                  setValue={(selectedId) =>
                    setFormData((prev) => ({
                      ...prev,
                      pop_id: selectedId,
                      nas_id: "",
                      olt_id: "",
                      central_odc_id: "",
                      odc_id: "",
                      odp_id: "",
                    }))
                  }
                  searchable={true}
                />

                <SelectNas
                  label="Nas Server - MikroTik"
                  placeholder="Silahkan pilih Nas Server"
                  pop_id={formData.pop_id}
                  value={formData.nas_id}
                  setValue={(selectedId) =>
                    setFormData((prev) => ({
                      ...prev,
                      nas_id: selectedId,
                      olt_id: "",
                      central_odc_id: "",
                      odc_id: "",
                      odp_id: "",
                    }))
                  }
                  searchable={true}
                />

                <SelectOlt
                  label="Olt"
                  placeholder="Silahkan pilih Olt"
                  pop_id={formData.pop_id}
                  nas_id={formData.nas_id}
                  value={formData.olt_id}
                  setValue={(selectedId) =>
                    setFormData((prev) => ({
                      ...prev,
                      olt_id: selectedId,
                      central_odc_id: "",
                      odc_id: "",
                      odp_id: "",
                    }))
                  }
                  searchable={true}
                />

                <SelectCentralOdc
                  label="Central ODC"
                  placeholder="Silahkan pilih Central ODC"
                  pop_id={formData.pop_id}
                  nas_id={formData.nas_id}
                  olt_id={formData.olt_id}
                  value={formData.central_odc_id}
                  setValue={(selectedId) =>
                    setFormData((prev) => ({
                      ...prev,
                      central_odc_id: selectedId,
                      odc_id: "",
                      odp_id: "",
                    }))
                  }
                  searchable={true}
                />

                <SelectOdc
                  label="Odc"
                  placeholder="Silahkan pilih Odc"
                  pop_id={formData.pop_id}
                  nas_id={formData.nas_id}
                  olt_id={formData.olt_id}
                  central_odc_id={formData.central_odc_id}
                  value={formData.odc_id}
                  setValue={(selectedId) =>
                    setFormData((prev) => ({
                      ...prev,
                      odc_id: selectedId,
                      nas_id: "",
                      odp_id: "",
                    }))
                  }
                  searchable={true}
                />

                <SelectOdp
                  label={"Pilih ODP"}
                  placeholder="Silahkan pilih ODP"
                  pop_id={formData.odc_id}
                  value={formData.odp_id}
                  searchable
                  setValue={(id) => setFormData((f) => ({ ...f, odp_id: id }))}
                />
              </div>
            </div>

            {/* ============================================ */}
            {/* SECTION 3: PETA LOKASI */}
            {/* ============================================ */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
              }}
            >
              <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                  <FaMapLocationDot className="text-cyan-500" /> Lokasi
                  Pelanggan
                </h3>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-500">
                    Lat:{" "}
                    <span className="text-cyan-400">
                      {formData.latitude?.toFixed(6) || "-"}
                    </span>
                  </span>
                  <span className="text-slate-500">
                    Lng:{" "}
                    <span className="text-cyan-400">
                      {formData.longitude?.toFixed(6) || "-"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Image & Location */}
            <div
              className="rounded-2xl p-1 border border-slate-700/50"
              style={{ background: "rgba(15, 23, 42, 0.5)" }}
            >
              <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                  <FaMapLocationDot className="text-cyan-500" /> Peta Lokasi
                </h3>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-500">
                    Lat:{" "}
                    <span className="text-cyan-400">
                      {formData.latitude.toFixed(5)}
                    </span>
                  </span>
                  <span className="text-slate-500">
                    Lng:{" "}
                    <span className="text-cyan-400">
                      {formData.longitude.toFixed(5)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-2">
                <div className="">
                  {/* Image */}
                  <div>
                    {previews.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase">
                          Akan Diunggah ({previews.length})
                        </label>
                        <div className="grid grid-cols-3 gap-2 p-2">
                          {previews.map((url, index) => (
                            <div
                              key={index}
                              className="relative group rounded-lg overflow-hidden border border-gray-800 aspect-square shadow-inner"
                            >
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Hapus"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {previews.length < 3 && (
                      <label
                        htmlFor="file-upload"
                        className="w-full border-2 border-dashed border-slate-600 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-700/30 hover:border-cyan-500/50 transition-all group mt-5 gap-3 py-2"
                      >
                        <FaPlus className="text-slate-500 group-hover:text-cyan-400 transition-colors" />
                        <span className="text-[9px] font-black text-slate-400 uppercase">
                          Upload Image
                        </span>
                        <input
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                          disabled={selectedFiles.length >= remainingSlots}
                        />
                      </label>
                    )}
                  </div>

                  <div className="mt-5 flex flex-col gap-3">
                    {/* ============================================ */}
                    {/* INPUT GPS GABUNGAN - FORMAT: lat, lng */}
                    {/* ============================================ */}
                    <div className="relative">
                      <label
                        htmlFor="gps-input"
                        className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5"
                      >
                        GPS
                      </label>
                      <div className="relative">
                        <input
                          id="gps-input"
                          type="text"
                          inputMode="decimal"
                          placeholder="-6.17511, 106.827153"
                          value={gpsInput}
                          onChange={handleGpsInputChange}
                          className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-20"
                          required
                        />
                        <button
                          type="button"
                          onClick={safeDetectGps}
                          disabled={loadingGps}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
                          title="Deteksi GPS"
                        >
                          {loadingGps ? (
                            <FiLoader className="animate-spin text-sm" />
                          ) : (
                            <FiCrosshair className="text-sm" />
                          )}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">
                        Format: latitude, longitude
                      </p>
                    </div>

                    <TextAreaDefault
                      id="address"
                      label="Detail Alamat"
                      placeholder="Alamat akan terisi otomatis..."
                      value={formData.address}
                      onChange={handleChange}
                    />

                    {/* Pesan Error GPS (jika ada) */}
                    {gpsError && (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <span className="text-amber-400 text-[11px] leading-relaxed">
                          ⚠️ {gpsError} — Menggunakan lokasi default. Klik peta
                          atau ikon crosshair untuk mengubah.
                        </span>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 mt-1">
                      * Klik peta untuk mengisi otomatis.
                    </p>
                  </div>
                </div>

                <div className="min-h-[400px] w-full relative z-0 rounded-xl overflow-clip">
                  {/* <MapContainer
                    center={[formData.latitude, formData.longitude]}
                    zoom={15}
                    scrollWheelZoom={true}
                    className="w-full h-full"
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapController data={formData} setData={setFormData} />
                  </MapContainer> */}
                  <MapView
                    center={[formData.latitude, formData.longitude]}
                    routes={[]}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapController data={formData} setData={setFormData}  className="right-[80px]"/>
                  </MapView>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* BUTTONS - DENGAN z-20 UNTUK DI ATAS MAP */}
            {/* ============================================ */}
            <div className="flex gap-4 pt-2 relative z-20">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-300 border border-slate-600 hover:bg-slate-800 transition-all uppercase tracking-widest"
              >
                Batal
              </button>
              <button
                type="submit"
                form="pppoe-form"
                disabled={loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-cyan-900/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FiLoader className="animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  "Simpan Data"
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};
